import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { Transaction, BatchUpdatePayload, BatchDeletePayload, BatchApplyRulePayload, BatchTagPayload, BatchOperationResult, ReviewQueue } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

type RawEntityObjectRef = { _id?: string; id?: string };
type RawCategoryObjectRef = RawEntityObjectRef & {
  name?: string;
  color?: string;
  emoji?: string;
};
type RawEntityRef = string | RawEntityObjectRef | null | undefined;
type RawCategoryRef = string | RawCategoryObjectRef | null | undefined;
type RawItem = {
  description: string;
  amount: number;
  discountAmount?: number;
  quantity?: number;
  notes?: string;
  emoji?: string;
  category?: RawCategoryRef;
};
type RawReceipt = {
  filename: string;
  path: string;
  mimetype: string;
  size: number;
};
type RawTransaction = Omit<Transaction, 'id' | 'accountId' | 'category' | 'categoryId' | 'merchant' | 'items' | 'hasItems' | 'receiptAttachment'> & {
  _id?: string;
  id?: string;
  account?: RawEntityRef;
  category?: RawCategoryRef;
  merchant?: string | { name?: string };
  items?: RawItem[];
  receipt?: RawReceipt;
  hasItems?: boolean;
};

function getEntityId(ref: RawEntityRef): string | undefined {
  if (typeof ref === 'string') return ref;
  if (!ref) return undefined;
  return ref._id || ref.id;
}

function isCategoryObject(ref: RawCategoryRef): ref is RawCategoryObjectRef {
  return typeof ref === 'object' && ref !== null;
}

// [T27P1] Normalize backend transaction response to match frontend Transaction type.
// Backend returns populated objects (account: {_id, name}, category: {_id, name, ...})
// while frontend expects flat strings (accountId, categoryId, category as name string).
// [T48P0] Guard against null populated refs — Mongoose populate() returns null when
// the referenced document (account/category) has been deleted. Since
// typeof null === 'object' in JS, an unguarded access like null._id would throw a
// TypeError and crash the entire fetchTransactions thunk.
function normalizeTransaction(txn: RawTransaction): Transaction {
  // T80P1: Normalize populated item categories
  const items = Array.isArray(txn.items)
    ? txn.items.map((item) => ({
        description: item.description,
        amount: item.amount,
        discountAmount: item.discountAmount ?? undefined,
        quantity: item.quantity ?? undefined,
        notes: item.notes || undefined,
        emoji: item.emoji || undefined,
        categoryId: isCategoryObject(item.category)
          ? (item.category._id || item.category.id)
          : (item.category || undefined),
        categoryName: isCategoryObject(item.category)
          ? item.category.name
          : undefined,
        categoryColor: isCategoryObject(item.category)
          ? item.category.color
          : undefined,
        categoryEmoji: isCategoryObject(item.category)
          ? item.category.emoji
          : undefined,
      }))
    : undefined;

  const accountId = getEntityId(txn.account) || '';
  const categoryId = isCategoryObject(txn.category)
    ? (txn.category._id || txn.category.id)
    : (txn.category || undefined);
  const category = isCategoryObject(txn.category)
    ? txn.category.name
    : (txn.category || undefined);

  return {
    ...txn,
    id: txn._id || txn.id || '',
    accountId,
    categoryId,
    // Keep category as display name string for dashboard/list display
    category,
    // Pass through emoji if present
    emoji: txn.emoji || undefined,
    // T156: Flatten merchant object → string (backend may return { name, id })
    merchant: txn.merchant && typeof txn.merchant === 'object'
      ? txn.merchant.name ?? undefined
      : txn.merchant,
    // T80P1: Include normalized items
    items,
    discountAmount: txn.discountAmount ?? undefined,
    hasItems:
      typeof txn.hasItems === 'boolean'
        ? txn.hasItems
        : Array.isArray(txn.items) && txn.items.length > 0,
    // T130: Map backend `receipt` embedded subdocument → frontend `receiptAttachment`
    receiptAttachment: txn.receipt && txn.receipt.filename
      ? { filename: txn.receipt.filename, path: txn.receipt.path, mimetype: txn.receipt.mimetype, size: txn.receipt.size }
      : undefined,
  };
}

// [T27P1] Map frontend field names to backend field names for create/update requests.
// Frontend uses accountId/categoryId (matching Transaction type), backend Zod schema
// expects account/category (matching Mongoose model field names).
function toBackendPayload(data: Partial<Transaction>): Record<string, unknown> {
  const { accountId, categoryId, ...rest } = data;
  const sanitizedRest: Record<string, unknown> = { ...rest };
  delete sanitizedRest.id;
  delete sanitizedRest.userId;
  delete sanitizedRest.hasItems;
  // T80P1: Map item categoryId → category for backend
  const items = Array.isArray(data.items)
    ? data.items.map((item) => ({
        description: item.description,
        amount: item.amount,
      ...(item.discountAmount != null ? { discountAmount: item.discountAmount } : {}),
        ...(item.quantity != null ? { quantity: item.quantity } : {}),
        ...(item.categoryId ? { category: item.categoryId } : {}),
        ...(item.notes ? { notes: item.notes } : {}),
        ...(item.emoji ? { emoji: item.emoji } : {}),
      }))
    : undefined;
  return {
    ...sanitizedRest,
    ...(accountId && { account: accountId }),
    ...(categoryId !== undefined ? { category: categoryId || null } : {}),
    ...(items !== undefined ? { items } : {}),
  };
}

interface TransactionsState {
  transactions: Transaction[];
  recurringTransactions: Transaction[];
  isLoading: boolean;
  error: string | null;
  reviewQueue: ReviewQueue | null;
  filters: {
    accountId?: string;
    category?: string;
    type?: 'income' | 'expense' | 'transfer';
    startDate?: string;
    endDate?: string;
    searchTerm?: string;
  };
  pagination: {
    currentPage: number;
    pageSize: number;
    totalItems: number;
  };
  // [T-76e90519] Global All-range bounds across ALL matching transactions for
  // the current user + non-date filters (server-computed, excludes the active
  // `dateFrom`/`dateTo` predicate). ISO 8601 strings. `null` for either bound
  // means no valid transactions were returned for the current filter set, so
  // the All label must keep its truthful "All dates" placeholder.
  dateBounds: { min: string | null; max: string | null };
}

const initialState: TransactionsState = {
  transactions: [],
  recurringTransactions: [],
  isLoading: false,
  error: null,
  reviewQueue: null,
  filters: {},
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
  },
  // [T-76e90519] `null/null` until the first list response lands.
  dateBounds: { min: null, max: null },
};

// Async thunks
export const fetchTransactions = createAsyncThunk(
  'transactions/fetchTransactions',
  async (
    params: {
      page?: number;
      limit?: number;
      accountId?: string;
      categoryId?: string;
      type?: string;
      dateFrom?: string;
      dateTo?: string;
      search?: string;
      sortBy?: string;
      sortOrder?: string;
      timezone?: string;
    } | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(endpoints.transactions.list, { params });
      // Backend returns { success, data: [...], total, page, totalPages,
      // dateBounds: { min: ISOString|null, max: ISOString|null } }
      const body = response.data;
      // [T-76e90519] Carry the optional global bounds into Redux. Older
      // backends (pre-fix) won't ship this field — guard with a default so the
      // selector never breaks for users who haven't redeployed.
      const rawBounds = body.dateBounds as
        | { min?: string | null; max?: string | null }
        | undefined;
      const dateBounds = {
        min:
          rawBounds && typeof rawBounds.min === 'string' ? rawBounds.min : null,
        max:
          rawBounds && typeof rawBounds.max === 'string' ? rawBounds.max : null,
      };
      return {
        // [T27P1] Normalize populated objects from backend to flat IDs for frontend
        transactions: (body.data || []).map(normalizeTransaction) as Transaction[],
        totalItems: body.total as number,
        page: body.page as number,
        totalPages: body.totalPages as number,
        dateBounds,
      };
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch transactions'));
    }
  }
);

export const createTransaction = createAsyncThunk(
  'transactions/createTransaction',
  async (transactionData: Partial<Transaction>, { rejectWithValue }) => {
    try {
      // [T27P1] Map frontend accountId/categoryId → backend account/category
      const payload = toBackendPayload(transactionData);
      const response = await api.post(endpoints.transactions.create, payload);
      return normalizeTransaction(response.data.data) as Transaction;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create transaction'));
    }
  }
);

export const updateTransaction = createAsyncThunk(
  'transactions/updateTransaction',
  async ({ id, data }: { id: string; data: Partial<Transaction> }, { rejectWithValue }) => {
    try {
      // [T27P1] Map frontend accountId/categoryId → backend account/category
      const payload = toBackendPayload(data);
      const response = await api.put(endpoints.transactions.update(id), payload);
      return normalizeTransaction(response.data.data) as Transaction;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update transaction'));
    }
  }
);

export const deleteTransaction = createAsyncThunk(
  'transactions/deleteTransaction',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(endpoints.transactions.delete(id));
      return id;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete transaction'));
    }
  }
);

export const bulkDeleteTransactions = createAsyncThunk(
  'transactions/bulkDeleteTransactions',
  async (ids: string[], { rejectWithValue }) => {
    try {
      await api.post(endpoints.transactions.bulkDelete, { ids });
      return ids;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete transactions'));
    }
  }
);

// [T114-F5] Batch operations thunks
export const batchUpdateTransactions = createAsyncThunk(
  'transactions/batchUpdate',
  async (payload: BatchUpdatePayload, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactions.batchUpdate, payload);
      return (response.data.data || response.data) as BatchOperationResult;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to batch update transactions'));
    }
  }
);

export const batchDeleteTransactions = createAsyncThunk(
  'transactions/batchDelete',
  async (payload: BatchDeletePayload, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactions.batchDelete, payload);
      return (response.data.data || response.data) as BatchOperationResult;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to batch delete transactions'));
    }
  }
);

export const batchApplyRule = createAsyncThunk(
  'transactions/batchApplyRule',
  async (payload: BatchApplyRulePayload, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactions.batchApplyRule, payload);
      return (response.data.data || response.data) as BatchOperationResult;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to apply rule to transactions'));
    }
  }
);

export const batchTagTransactions = createAsyncThunk(
  'transactions/batchTag',
  async (payload: BatchTagPayload, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactions.batchTag, payload);
      return (response.data.data || response.data) as BatchOperationResult;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to batch tag transactions'));
    }
  }
);

export const fetchRecurringTransactions = createAsyncThunk(
  'transactions/fetchRecurringTransactions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.transactions.recurring);
      return (response.data.data || []).map(normalizeTransaction) as Transaction[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch recurring transactions'));
    }
  }
);

export const processRecurringTransactions = createAsyncThunk(
  'transactions/processRecurringTransactions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactions.processRecurring);
      return response.data.data as { generatedCount: number; generatedIds: string[] };
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to process recurring transactions'));
    }
  }
);

export const exportTransactions = createAsyncThunk(
  'transactions/exportTransactions',
  async (
    params: { format?: 'csv' | 'json'; dateFrom?: string; dateTo?: string } | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(endpoints.transactions.export, {
        params,
        responseType: params?.format === 'json' ? 'json' : 'blob',
      });

      if (params?.format === 'json') {
        return response.data;
      }

      // CSV — trigger browser download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fintrack-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { downloaded: true };
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to export transactions'));
    }
  }
);

export const duplicateTransaction = createAsyncThunk(
  'transactions/duplicate',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactions.duplicate(id));
      return normalizeTransaction(response.data.data) as Transaction;
    } catch (err: unknown) {
      return rejectWithValue(getApiErrorMessage(err, 'Failed to duplicate'));
    }
  }
);

export const transferBetweenAccounts = createAsyncThunk(
  'transactions/transferBetweenAccounts',
  async (
    data: {
      fromAccountId: string;
      toAccountId: string;
      amount: number;
      description: string;
      date: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(endpoints.transactions.transfer, data);
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to transfer between accounts'));
    }
  }
);

// [T155P3] Fetch review queue for Data Health Hub
export const fetchReviewQueue = createAsyncThunk(
  'transactions/fetchReviewQueue',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.transactions.reviewQueue);
      return (response.data.data || response.data) as ReviewQueue;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch review queue'));
    }
  }
);

// Slice
const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<TransactionsState['filters']>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1; // Reset to first page when filters change
      // [T-76e90519] P2 follow-up — setFilters changes query identity
      // (any combination of accountId/categoryId/type/status/searchTerm/
      // startDate/endDate). The server-computed `dateBounds` aggregate is
      // scoped to the authenticated user + the active non-date filters, so
      // the cached bounds are STALE the moment a query-identity-changing
      // filter is dispatched. Clear to `{ min: null, max: null }` so the
      // All/Start/End cards fall back to the truthful "All dates"
      // placeholder until the next successful fetch. The `pending` /
      // `rejected` reducers below already clear too — this closes the
      // pre-fetch gap for an in-process filter change that does not yet
      // result in a fetchTransactions call.
      state.dateBounds = { min: null, max: null };
    },
    clearFilters: (state) => {
      state.filters = {};
      state.pagination.currentPage = 1;
      // [T-76e90519] P2 follow-up — clearFilters also changes query
      // identity (resets to the no-filter baseline). Same justification as
      // setFilters above: the cached bounds describe a different filter
      // set, so we must clear to avoid surfacing the prior period's
      // endpoints while the next request is in-flight.
      state.dateBounds = { min: null, max: null };
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    // [T68P1-c] Optimistic update actions for offline sync
    addOptimisticTransaction: (state, action: PayloadAction<Transaction>) => {
      state.transactions.unshift(action.payload);
    },
    replaceOptimisticTransaction: (state, action: PayloadAction<{ optimisticId: string; realItem: Transaction }>) => {
      const idx = state.transactions.findIndex(t => t.id === action.payload.optimisticId);
      if (idx !== -1) state.transactions[idx] = action.payload.realItem;
    },
    removeOptimisticTransaction: (state, action: PayloadAction<string>) => {
      state.transactions = state.transactions.filter(t => t.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    // Fetch transactions
    builder
      .addCase(fetchTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        // [RADIX-RESIZE-001 / P3 bounded] Clear `dateBounds` on every pending
        // transition so the All-range cards in the open popover can't display
        // stale endpoints from the previous filter while the new request is
        // in-flight. The `fulfilled` reducer below restores them from the
        // fresh response; if the request fails, `rejected` keeps them
        // cleared. Without this, a fast period toggle could leave the
        // popover showing the prior period's bounds for the ~50-300ms the new
        // request takes. The cards fall back to the `All dates` placeholder
        // when `dateBounds.min/max` are both null.
        state.dateBounds = { min: null, max: null };
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transactions = action.payload.transactions;
        state.pagination.totalItems = action.payload.totalItems;
        // [T-76e90519] Server-computed global All-range bounds (ISO strings).
        state.dateBounds = action.payload.dateBounds;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        // [P3 bounded] Mirror the pending-clear so a failed request never
        // surfaces the prior period's bounds. `dateBounds` returns to its
        // initial `{ min: null, max: null }` shape so the All cards show
        // the truthful placeholder until the next successful fetch.
        state.dateBounds = { min: null, max: null };
      });

    // Create transaction
    builder
      .addCase(createTransaction.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTransaction.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transactions.unshift(action.payload);
        state.pagination.totalItems += 1;
      })
      .addCase(createTransaction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update transaction
    builder
      .addCase(updateTransaction.fulfilled, (state, action) => {
        const index = state.transactions.findIndex(txn => txn.id === action.payload.id);
        if (index !== -1) {
          state.transactions[index] = action.payload;
        }
      })
      .addCase(updateTransaction.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete transaction
    builder
      .addCase(deleteTransaction.fulfilled, (state, action) => {
        state.transactions = state.transactions.filter(txn => txn.id !== action.payload);
        state.pagination.totalItems -= 1;
      })
      .addCase(deleteTransaction.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Bulk delete transactions
    builder
      .addCase(bulkDeleteTransactions.fulfilled, (state, action) => {
        state.transactions = state.transactions.filter(txn => !action.payload.includes(txn.id));
        state.pagination.totalItems -= action.payload.length;
      })
      .addCase(bulkDeleteTransactions.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Fetch recurring transactions
    builder
      .addCase(fetchRecurringTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRecurringTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.recurringTransactions = action.payload;
      })
      .addCase(fetchRecurringTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Export transactions
    builder
      .addCase(exportTransactions.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Duplicate transaction
    builder
      .addCase(duplicateTransaction.fulfilled, (state, action) => {
        state.transactions.unshift(action.payload);
        state.pagination.totalItems += 1;
      })
      .addCase(duplicateTransaction.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // [T155P3] Fetch review queue
    builder
      .addCase(fetchReviewQueue.fulfilled, (state, action) => {
        state.reviewQueue = action.payload;
      })
      .addCase(fetchReviewQueue.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilters, clearFilters, setPage, setPageSize, clearError,
  addOptimisticTransaction, replaceOptimisticTransaction, removeOptimisticTransaction,
} = transactionsSlice.actions;
export default transactionsSlice.reducer;
